import {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {FixedIndicator} from '@app/types';

import {CreateIndicatorSchemaType} from '../components/indicator-edit-form';
import {mapFilterValuesToLabels, mapIndicatorIdToDisplayText} from '../utils/change-mapper';
import {Change, deepDiff} from '../utils/object-diff';

/**
 * Hook pour gérer la détection et l'affichage des changements d'indicateurs
 */
export const useIndicatorChanges = (fixedIndicators: FixedIndicator[]) => {
  const {t} = useTranslation();
  const [changes, setChanges] = useState<Record<string, Change>>({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showNoChangesModal, setShowNoChangesModal] = useState(false);

  /**
   * Convertit un ID d'indicateur en texte d'affichage
   */
  const getIndicatorDisplayText = useCallback(
    (id: number): string => {
      if (id === 0) return t('pages.pilotage-rh.indicators.form.custom');
      const indicator = fixedIndicators?.find((i) => i.id === id);
      return indicator ? indicator.className : id.toString();
    },
    [fixedIndicators, t],
  );

  /**
   * Détecte et traite les changements entre deux configurations d'indicateur
   */
  const handleChangesDetection = useCallback(
    (original: CreateIndicatorSchemaType, current: CreateIndicatorSchemaType): boolean => {
      const detectedChanges = deepDiff(original, current, t);
      const hasChanges = Object.keys(detectedChanges).length > 0;

      if (hasChanges) {
        // Transformer les IDs d'indicateurs en texte lisible
        mapIndicatorIdToDisplayText(detectedChanges, getIndicatorDisplayText);

        // Transformer les valeurs de filtres en labels
        mapFilterValuesToLabels(detectedChanges, t);

        setChanges(detectedChanges);
        setShowConfirmModal(true);
      } else {
        setShowNoChangesModal(true);
      }

      return hasChanges;
    },
    [t, getIndicatorDisplayText],
  );

  return {
    changes,
    showConfirmModal,
    showNoChangesModal,
    handleChangesDetection,
    setShowConfirmModal,
    setShowNoChangesModal,
  };
};
